import type { PickingInfo } from "@deck.gl/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/sharp-regular-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useCopyToClipboard, useDebounce } from "@uidotdev/usehooks";
import type { BBox } from "geojson";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapExtraDataSurveysListDataTypeEnum } from "../../apiClient/generated";
import { useHasFlag, useUser } from "../../auth";
import {
    useEmissionRecordsApiClient,
    useGeoDataApi,
    useMapApiClient,
} from "../../hooks";
import { EMISSION_LAYER_STYLES } from "./constants";
import { MapDataContext } from "./context";
import { ControlContainer } from "./elements/common";
import { DashboardControls } from "./elements/DataControlsV2";
import { EmissionsBrushChart } from "./elements/EmissionsBrushChart";
import { MapScale } from "./elements/MapScale";
import { ZoomAndLegend } from "./elements/ZoomAndLegend";
import { useAnalyticsOnMap } from "./hooks/analyticsLayer";
import { useDrawAndFilterControlsV2 } from "./hooks/areaFilter";
import {
    useEmissionRecordsOnMap,
    usePublicDataPointsOnMap,
    useUnmatchedDataPointsOnMap,
} from "./hooks/emissions";
import { useExtraDataOnMap } from "./hooks/extra";
import {
    useInfrastructureOnMap,
    usePipelinesOnMap,
} from "./hooks/infrastructure";
import { useInitialMapStateFromParams } from "./hooks/initialMapState";
import { useMapData, useMapFilters } from "./hooks/mapDataAndFilters";
import { useMap } from "./hooks/mapState";
import { useMapMeasuring } from "./hooks/measuring";
import { MarkerLayer } from "./layers/markers";
import { MapBase } from "./Map";
import { MapPopup, type PickedItems } from "./MapPopup";
import { automaticPanAndZoomAtom } from "./state";
import { LoadingIndicator } from "./ui/LoadingIndicator";

export const MapViewV2 = () => {
    // Utilities
    const [, copyToClipboard] = useCopyToClipboard();
    const [autoMove] = useAtom(automaticPanAndZoomAtom);

    // Compute static initial state from params
    const {
        hasInitialState,
        initialBasemap,
        initialMapState,
        initialEmissionId,
        initialFilterState,
        initialSelectedContext,
    } = useInitialMapStateFromParams();

    // Map state: basemap + viewstate data coming from
    // base map component and state.
    const {
        _viewState,
        debounced,
        basemap,
        flyTo,
        flyToBBox,
        zoomIn,
        zoomOut,
    } = useMap("mainMap", {
        basemap: initialBasemap,
        _viewState: { ...initialMapState, bearing: 0, pitch: 0 },
    });

    // Handles storing the `basemap` on localstorage
    // and updating the value as it changes.
    useEffect(() => {
        if (basemap) {
            localStorage.setItem("basemap", basemap as string);
        } else {
            localStorage.removeItem("basemap");
        }
    }, [basemap]);

    // Drawing controls & filter using area state
    const { isDrawing, drawingLayer, filterByArea } =
        useDrawAndFilterControlsV2("mainMap");

    // Measurement
    const { measureLayers, isMeasuring, toggleMeasuring } = useMapMeasuring({
        enabled: true,
    });

    // Map context states
    // This is only used in the main map to display the
    // sidebar and hide/show plumes.
    const { setSelectedContext, selectedContext } = useMapData("mainMap", {
        selectedContext: initialSelectedContext.selectedContext,
    });

    /**
     * Map data filter values.
     *
     * Stores the values for the filters selected in the map:
     * - Infrastructure: type of infrastructure, show aerial images, etc.
     * - Emissions: Date ranges, provider and type filters.
     */
    const { filterState, updateMapFilterState } = useMapFilters(
        "mainMap",
        initialFilterState,
    );

    /**
     * Active computation of initial states, used when we
     * need to grab something from the backend to know where
     * to point the map at.
     */

    // If there's a initial emission id, fetch emission data
    // then set date range based on emission.
    // Position information should already come in the URL
    // as `lat` and `lon`.
    const emissionsApiClient = useEmissionRecordsApiClient();
    const alreadyMovedEmission = useRef(false);
    const initialEmissionQuery = useQuery({
        queryKey: ["emissionRecord", initialEmissionId],
        queryFn: async () => {
            return await emissionsApiClient.emissionRecordsRetrieve({
                id: initialEmissionId,
            });
        },
        enabled: !!initialEmissionId,
    });
    useEffect(() => {
        if (
            !alreadyMovedEmission.current &&
            initialEmissionQuery.data &&
            initialEmissionId
        ) {
            alreadyMovedEmission.current = true;
            // Get original filterState date range
            const startDate = new Date(filterState.emissions.startDateFilter);
            const endDate = new Date(filterState.emissions.endDateFilter);
            const timeRange = endDate.getTime() - startDate.getTime();

            // Set time range, centering on selected emission time
            const newStartDate = new Date(
                initialEmissionQuery.data.dataPoint.detectionTimestamp.getTime() -
                    timeRange / 2,
            );
            const newEndDate = new Date(
                initialEmissionQuery.data.dataPoint.detectionTimestamp.getTime() +
                    timeRange / 2,
            );
            // Update date range so emission shows up
            updateMapFilterState((d) => {
                d.emissions.startDateFilter = newStartDate.toISOString();
                d.emissions.endDateFilter = newEndDate.toISOString();
            });
            // Select related plumes.
            setSelectedContext({
                emissionRecordId: initialEmissionQuery.data.id,
                relatedPlume: initialEmissionQuery.data.dataPoint.plumeImage,
            });
        }
    }, [
        initialEmissionQuery.data,
        initialEmissionId,
        filterState.emissions.startDateFilter,
        filterState.emissions.endDateFilter,
        updateMapFilterState,
        setSelectedContext,
    ]);

    // If there's no selected context: zoom the user to their
    // geofence, or infrastructure if they don't have one.
    const alreadyCenteredUser = useRef(hasInitialState);
    const geoDataApiClient = useGeoDataApi();
    const mapApiClient = useMapApiClient();
    const userInfo = useUser();
    useEffect(() => {
        const getUserGeofence = async () => {
            let userGeofencesBbox: number[];
            if (userInfo.geofenceAreas.length > 0) {
                userGeofencesBbox = (
                    await geoDataApiClient.geodataItemsBboxRetrieve({
                        ids: userInfo.geofenceAreas,
                    })
                ).extent;
            } else {
                userGeofencesBbox = (await mapApiClient.mapSitesBboxRetrieve())
                    .extent;
            }
            if (userGeofencesBbox) {
                flyToBBox(userGeofencesBbox as BBox);
            }
        };
        if (
            !alreadyCenteredUser.current &&
            debounced.viewState &&
            !userInfo.isStaff
        ) {
            alreadyCenteredUser.current = true;
            getUserGeofence();
        }
    }, [
        debounced.viewState,
        userInfo,
        flyToBBox,
        geoDataApiClient,
        mapApiClient,
    ]);

    /**
     * Map click handling and context selection section.
     *
     * This is where the map inteligently handles when the user
     * clicks on the map.
     *
     * - If it's a single item, them open sidebar immediately.
     * - If it's nothing, deselect and close sidebar.
     * - If it's more than one item, show a sidebar with options.
     */

    // Store list of items picked when user clicks on screen.
    const [pickedItems, setPickedItems] = useState<PickedItems>();

    /**
     * pickItem
     *
     * Used to handle setting the selected context.
     * It decides if we need to hide a plume and what kind
     * of layer is selected and applied the appropriate
     * data filters.
     */
    const pickItem = useCallback(
        (item: PickingInfo) => {
            let extraDataId: string;
            let relatedPlume: string;
            const layerId = item.layer.id;
            const itemProperties = item.object.properties;
            if (
                !layerId.includes("infrastructure") &&
                layerId !== "pipelines"
            ) {
                if (itemProperties.plumeImage) {
                    relatedPlume = itemProperties.plumeImage;
                } else if (
                    itemProperties.geometry ||
                    !itemProperties.plumeImage
                ) {
                    relatedPlume = null;
                }
            }

            if (layerId.includes("extra-data-clusters")) {
                flyTo(
                    item.coordinate[1],
                    item.coordinate[0],
                    itemProperties.zoom,
                    true,
                );
                return;
            }

            if (autoMove && layerId.includes("-clusters")) {
                flyTo(
                    item.coordinate[1],
                    item.coordinate[0],
                    itemProperties.zoom,
                    true,
                );
                return;
            }

            let siteId: string;
            if (layerId.includes("infrastructure-sites")) {
                siteId = itemProperties.id;
            } else if (layerId.includes("infrastructure-equipment")) {
                siteId = itemProperties.site;
            }

            // Extra data
            if (layerId.includes("extra-data")) {
                extraDataId = itemProperties.id;
            }

            setSelectedContext({
                siteId,
                emissionRecordId:
                    (layerId.includes("emission-points") ||
                        layerId.includes("emission-crosshairs")) &&
                    !layerId.includes("datapoint")
                        ? itemProperties.id
                        : undefined,
                dataPointId:
                    layerId.includes("datapoint") &&
                    !layerId.includes("unmatched")
                        ? itemProperties.id
                        : undefined,
                unmatchedDataPointId: layerId.includes("unmatched-datapoint")
                    ? itemProperties.id
                    : undefined,
                relatedPlume,
                pipeline:
                    layerId === "pipelines-v2"
                        ? {
                              id: itemProperties.id,
                              coordinates: item.coordinate,
                          }
                        : undefined,
                dateRangeToShowData: selectedContext.dateRangeToShowData,
                extraDataId,
            });
            setPickedItems(undefined);
        },
        [flyTo, setSelectedContext, selectedContext, autoMove],
    );

    /**
     * onClickMap
     *
     * Callback for the map's picking event on click, handles the different
     * cases and set's the `pickedItems` value accordingly.
     */
    const onClickMap = useCallback(
        (info: PickingInfo[], event: React.MouseEvent<HTMLDivElement>) => {
            if (isMeasuring || isDrawing) {
                return;
            }

            const filteredInfo = info.filter(
                (i) => i.layer.id !== "drawing-layer",
            );
            // If nothing was clicked, clear selection
            if (filteredInfo.length === 0) {
                setPickedItems(undefined);
                setSelectedContext({
                    dateRangeToShowData: selectedContext.dateRangeToShowData,
                });
            }
            // If only one item is picked, select context immediately.
            else if (
                filteredInfo.length === 1 &&
                filteredInfo[0].layer.id !== "infrastructure-sites-clusters"
            ) {
                pickItem(filteredInfo[0]);
            }
            // Else, set items and display tooltip at user cursor position.
            else {
                // Compute clicked emissions
                const sites = filteredInfo.filter(
                    (i) =>
                        i.layer.id.includes("infrastructure-sites-points") ||
                        i.layer.id.includes("infrastructure-sites-shapes"),
                );
                const siteClusters = filteredInfo.filter((i) =>
                    i.layer.id.includes("infrastructure-sites-clusters"),
                );
                const pipelines = filteredInfo.filter(
                    (i) => i.layer.id === "pipelines-v2",
                );
                const emissions = filteredInfo.filter(
                    (i) =>
                        (i.layer.id.includes("emission-points") ||
                            i.layer.id.includes("emission-crosshairs")) &&
                        !i.layer.id.includes("datapoint"),
                );
                const emissionClusters = filteredInfo.filter((i) =>
                    i.layer.id.includes("-emission-clusters"),
                );
                const extraDataClusters = filteredInfo.filter((i) =>
                    i.layer.id.includes("extra-data-clusters"),
                );
                const extraData = filteredInfo.filter(
                    (i) =>
                        i.layer.id.includes("extra-data") &&
                        !i.layer.id.includes("extra-data-clusters"),
                );

                // Extra-data clusters zoom to their expansion level instead of
                // opening a popup.
                if (extraDataClusters.length > 0) {
                    const cluster = extraDataClusters[0];
                    flyTo(
                        cluster.coordinate[1],
                        cluster.coordinate[0],
                        cluster.object.properties.zoom,
                        true,
                    );
                    return;
                }

                // If there's only one emission (ignore data points), show modal directly
                if (
                    sites.length === 0 &&
                    pipelines.length === 0 &&
                    emissionClusters.length === 0 &&
                    emissions.length === 1
                ) {
                    pickItem(emissions[0]);
                } else {
                    // Else set state and show modal.
                    setPickedItems({
                        pointClicked: {
                            x: event.clientX,
                            y: event.clientY,
                        },
                        sites,
                        siteClusters,
                        equipment: [],
                        pipelines,
                        emissions,
                        emissionClusters,
                        dataPoints:
                            emissions.length === 0
                                ? filteredInfo.filter(
                                      (i) =>
                                          i.layer.id.includes("datapoint") &&
                                          !i.layer.id.includes("unmatched"),
                                  )
                                : [],
                        unmatchedDataPoints:
                            emissions.length === 0
                                ? filteredInfo.filter((i) =>
                                      i.layer.id.includes(
                                          "unmatched-datapoint",
                                      ),
                                  )
                                : [],
                        extraData,
                    });
                }
            }
        },
        [
            isMeasuring,
            isDrawing,
            pickItem,
            setSelectedContext,
            selectedContext,
            flyTo,
        ],
    );

    // If the map changes (zoom, pan, drag) then clear `pickedItems`.
    // This is a side effect to remove the dropdown if the viewset changes.
    useEffect(() => {
        setPickedItems(undefined);
    }, [_viewState]);

    // Map data fetching
    // FIXME: too much repetition here, this should be refactored to be simpler,
    // maybe useAerscapeData handling all the data fetching and it's results being passed
    // to useAerscape layers to avoid spreading results everywhere here.
    const infrastructureMapData = useInfrastructureOnMap(
        "mainMap",
        filterState.infrastructure.showInfrastructure &&
            filterState.infrastructure.infraTypeFilter.length > 0,
        filterState.infrastructure.infraTypeFilter,
        filterByArea,
        useMemo(
            () => ({
                start:
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined,
                end:
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined,
            }),
            [
                filterState.emissions.startDateFilter,
                filterState.emissions.endDateFilter,
            ],
        ),
    );

    // Extra data providers (e.g. ChampionX surveys)
    const extraDataMapData = useExtraDataOnMap(
        filterState.extraData.CHAMPION_X,
        "mainMap",
        useMemo(
            () => ({
                dataType: filterState.extraData.CHAMPION_X
                    ? [MapExtraDataSurveysListDataTypeEnum.ChampionX]
                    : [],
                startDatetimeBefore:
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined,
                endDatetimeAfter:
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined,
            }),
            [
                filterState.extraData.CHAMPION_X,
                filterState.emissions.startDateFilter,
                filterState.emissions.endDateFilter,
            ],
        ),
        filterByArea,
    );

    // New pipeline data loader
    const pipelineMapData = usePipelinesOnMap(
        filterState.infrastructure.showInfrastructure &&
            filterState.infrastructure.pipelineProduct.length > 0 &&
            filterState.infrastructure.pipelineType.length > 0,
        filterState.infrastructure.pipelineProduct,
        filterState.infrastructure.pipelineType,
        // TODO: move filtering to backend when using presets
        filterByArea,
    );

    // Retriving map emission data and computing layers.
    // Third Party
    const emissionsThirdParty = useEmissionRecordsOnMap(
        "mainMap",
        filterState.emissions.showEmissions,
        "thirdParty",
        true,
        useMemo(() => {
            const dateFilter = {};
            if (filterState.emissions.filterEmissionsBy === "detection_date") {
                dateFilter["detectionDateRangeAfter"] =
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined;
                dateFilter["detectionDateRangeBefore"] =
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined;
            } else {
                dateFilter["emissionMatchedAfter"] =
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined;
                dateFilter["emissionMatchedBefore"] =
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined;
            }

            return {
                ...dateFilter,
                providerWithSource:
                    filterState.emissions.emissionGroupFiltersV2.thirdParty,
                dataSource: ["THIRD_PARTY"],
                eventStatus: filterState.emissions.eventStatus,
                detectedRateMin: filterState.emissions.detectedRateMin
                    ? filterState.emissions.detectedRateMin * 1000
                    : undefined,
                detectedRateMax: filterState.emissions.detectedRateMax
                    ? filterState.emissions.detectedRateMax * 1000
                    : undefined,
            };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            filterState.emissions.emissionGroupFiltersV2.thirdParty,
            filterState.emissions.detectedRateMin,
            filterState.emissions.detectedRateMax,
            filterState.emissions.filterEmissionsBy,
            filterState.emissions.eventStatus,
        ]),
        EMISSION_LAYER_STYLES.thirdParty,
        filterByArea,
    );

    // Self reported (my company monitoring)
    const emissionsSelfReported = useEmissionRecordsOnMap(
        "mainMap",
        filterState.emissions.showEmissions,
        "selfReported",
        true,
        useMemo(() => {
            const dateFilter = {};
            if (filterState.emissions.filterEmissionsBy === "detection_date") {
                dateFilter["detectionDateRangeAfter"] =
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined;
                dateFilter["detectionDateRangeBefore"] =
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined;
            } else {
                dateFilter["emissionMatchedAfter"] =
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined;
                dateFilter["emissionMatchedBefore"] =
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined;
            }

            return {
                ...dateFilter,
                providerWithSource:
                    filterState.emissions.emissionGroupFiltersV2.selfReported,
                dataSource: ["SELF_REPORTED"],
                eventStatus: filterState.emissions.eventStatus,
                detectedRateMin: filterState.emissions.detectedRateMin
                    ? filterState.emissions.detectedRateMin * 1000
                    : undefined,
                detectedRateMax: filterState.emissions.detectedRateMax
                    ? filterState.emissions.detectedRateMax * 1000
                    : undefined,
            };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            filterState.emissions.emissionGroupFiltersV2.selfReported,
            filterState.emissions.detectedRateMin,
            filterState.emissions.detectedRateMax,
            filterState.emissions.filterEmissionsBy,
            filterState.emissions.eventStatus,
        ]),
        EMISSION_LAYER_STYLES.operatorProvided,
        filterByArea,
    );

    // EPA SEP data
    const emissionsEpa = useEmissionRecordsOnMap(
        "mainMap",
        filterState.emissions.showEmissions,
        "epa",
        true,
        useMemo(() => {
            const dateFilter = {};
            if (filterState.emissions.filterEmissionsBy === "detection_date") {
                dateFilter["detectionDateRangeAfter"] =
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined;
                dateFilter["detectionDateRangeBefore"] =
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined;
            } else {
                dateFilter["emissionMatchedAfter"] =
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined;
                dateFilter["emissionMatchedBefore"] =
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined;
            }

            return {
                ...dateFilter,
                providerWithSource:
                    filterState.emissions.emissionGroupFiltersV2.epa,
                dataSource: ["EPA"],
                detectedRateMin: filterState.emissions.detectedRateMin
                    ? filterState.emissions.detectedRateMin * 1000
                    : undefined,
                detectedRateMax: filterState.emissions.detectedRateMax
                    ? filterState.emissions.detectedRateMax * 1000
                    : undefined,
            };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            filterState.emissions.emissionGroupFiltersV2.epa,
            filterState.emissions.detectedRateMin,
            filterState.emissions.detectedRateMax,
            filterState.emissions.filterEmissionsBy,
        ]),
        EMISSION_LAYER_STYLES.epa,
        filterByArea,
    );

    // Public emissions
    const publicEmissions = usePublicDataPointsOnMap(
        filterState.emissions.emissionGroupFiltersV2.thirdPartyPublic ===
            "all" ||
            filterState.emissions.emissionGroupFiltersV2.thirdPartyPublic
                .length > 0,
        "public_datapoint",
        useMemo(() => {
            return {
                detectionDateRangeAfter:
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined,
                detectionDateRangeBefore:
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined,
                providerWithSource:
                    filterState.emissions.emissionGroupFiltersV2
                        .thirdPartyPublic,
                dataSource: ["THIRD_PARTY"],
                detectedRateMin: filterState.emissions.detectedRateMin
                    ? filterState.emissions.detectedRateMin * 1000
                    : undefined,
                detectedRateMax: filterState.emissions.detectedRateMax
                    ? filterState.emissions.detectedRateMax * 1000
                    : undefined,
            };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            filterState.emissions.emissionGroupFiltersV2.thirdPartyPublic,
            filterState.emissions.detectedRateMin,
            filterState.emissions.detectedRateMax,
        ]),
        EMISSION_LAYER_STYLES.thirdPartyPublic,
        filterByArea,
    );

    // Unmatched data points (company monitoring pending matching)
    const canViewUnmatchedPoints = useHasFlag("unmatched_emissions");
    const unmatchedDataPoints = useUnmatchedDataPointsOnMap(
        canViewUnmatchedPoints &&
            (filterState.emissions.emissionGroupFiltersV2
                .unmatchedDataPoints === "all" ||
                filterState.emissions.emissionGroupFiltersV2.unmatchedDataPoints
                    .length > 0),
        "unmatched-datapoint",
        useMemo(() => {
            return {
                detectionDateRangeAfter:
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined,
                detectionDateRangeBefore:
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined,
                providerWithSource:
                    filterState.emissions.emissionGroupFiltersV2
                        .unmatchedDataPoints,
                detectedRateMin: filterState.emissions.detectedRateMin
                    ? filterState.emissions.detectedRateMin * 1000
                    : undefined,
                detectedRateMax: filterState.emissions.detectedRateMax
                    ? filterState.emissions.detectedRateMax * 1000
                    : undefined,
            };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            filterState.emissions.emissionGroupFiltersV2.unmatchedDataPoints,
            filterState.emissions.detectedRateMin,
            filterState.emissions.detectedRateMax,
        ]),
        EMISSION_LAYER_STYLES.unmatchedDataPoints,
        filterByArea,
    );

    const heatmapsEnabled = useHasFlag("enable_experimental_heatmaps");
    const analyticsHeatmap = useAnalyticsOnMap(
        "mainMap",
        filterState.analytics.emissionHeatmaps.enabled && heatmapsEnabled,
        useMemo(
            () => ({
                ...filterState.analytics.emissionHeatmaps.filters,
                timestampAfter:
                    filterState.emissions.startDateFilter !== ""
                        ? new Date(filterState.emissions.startDateFilter)
                        : undefined,
                timestampBefore:
                    filterState.emissions.endDateFilter !== ""
                        ? new Date(filterState.emissions.endDateFilter)
                        : undefined,
            }),
            [
                filterState.analytics.emissionHeatmaps.filters,
                filterState.emissions.startDateFilter,
                filterState.emissions.endDateFilter,
            ],
        ),
        useMemo(
            () => ({
                sourceData: filterState.analytics.emissionHeatmaps.sourceData,
                colorMap: filterState.analytics.emissionHeatmaps.colorMap,
                opacity: filterState.analytics.emissionHeatmaps.opacity,
            }),
            [
                filterState.analytics.emissionHeatmaps.sourceData,
                filterState.analytics.emissionHeatmaps.colorMap,
                filterState.analytics.emissionHeatmaps.opacity,
            ],
        ),
        filterByArea,
    );

    const layers = useMemo(() => {
        const layerArray = [
            infrastructureMapData.aerialImageLayers,
            [
                ...publicEmissions.plumeLayers,
                ...unmatchedDataPoints.plumeLayers,
                ...emissionsSelfReported.plumeLayers,
                ...emissionsThirdParty.plumeLayers,
                ...emissionsEpa.plumeLayers,
            ],
            pipelineMapData.layers,
            infrastructureMapData.infrastructureLayers,
            extraDataMapData.dataLayers,
            [
                ...publicEmissions.emissionLayers,
                ...unmatchedDataPoints.emissionLayers,
                ...emissionsSelfReported.emissionLayers,
                ...emissionsThirdParty.emissionLayers,
                ...emissionsEpa.emissionLayers,
            ],
            analyticsHeatmap.layer,
        ];

        if (selectedContext.selectedLocation) {
            layerArray.push(
                MarkerLayer([
                    {
                        type: "Feature",
                        geometry: selectedContext.selectedLocation,
                        properties: {},
                    },
                ]),
            );
        }

        return layerArray;
    }, [
        infrastructureMapData.aerialImageLayers,
        infrastructureMapData.infrastructureLayers,
        extraDataMapData.dataLayers,
        pipelineMapData.layers,
        publicEmissions.plumeLayers,
        publicEmissions.emissionLayers,
        emissionsSelfReported.plumeLayers,
        emissionsSelfReported.emissionLayers,
        emissionsThirdParty.plumeLayers,
        emissionsThirdParty.emissionLayers,
        emissionsEpa.plumeLayers,
        emissionsEpa.emissionLayers,
        unmatchedDataPoints.plumeLayers,
        unmatchedDataPoints.emissionLayers,
        selectedContext.selectedLocation,
        analyticsHeatmap.layer,
    ]);

    const emissionsLoading =
        emissionsThirdParty.loading ||
        emissionsSelfReported.loading ||
        emissionsEpa.loading ||
        publicEmissions.loading ||
        unmatchedDataPoints.loading;

    const loading =
        infrastructureMapData.loading ||
        pipelineMapData.loading ||
        emissionsThirdParty.loading ||
        emissionsSelfReported.loading ||
        emissionsEpa.loading ||
        publicEmissions.loading ||
        unmatchedDataPoints.loading ||
        analyticsHeatmap.loading ||
        extraDataMapData.loading;

    // Get container dimensions
    const containerRef = useRef<HTMLDivElement>();
    const [width, setWidth] = useState(0);
    const debouncedWidth = useDebounce(width, 150);
    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.contentRect) {
                    setWidth(entry.contentRect.width);
                }
            }
        });

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, []);

    // Render component
    return (
        <div ref={containerRef}>
            {/* UI layer */}
            <MapDataContext.Provider
                value={{
                    allData: {
                        thirdPartyEmissions: emissionsThirdParty.data,
                        selfReportedEmissions: emissionsSelfReported.data,
                        publicEmissions: publicEmissions.data,
                        unmatchedDataPoints: unmatchedDataPoints.data,
                    },
                    visibleOnMap: {
                        thirdPartyEmissions: emissionsThirdParty.dataOnScreen,
                        selfReportedEmissions:
                            emissionsSelfReported.dataOnScreen,
                        publicEmissions: publicEmissions.dataOnScreen,
                        unmatchedDataPoints: unmatchedDataPoints.dataOnScreen,
                    },
                    filteredByContext: {
                        thirdPartyEmissions:
                            emissionsThirdParty.dataFilteredByContext,
                        selfReportedEmissions:
                            emissionsSelfReported.dataFilteredByContext,
                        publicEmissions: publicEmissions.dataFilteredByContext,
                        unmatchedDataPoints:
                            unmatchedDataPoints.dataFilteredByContext,
                    },
                    loading: emissionsLoading,
                }}
            >
                <div
                    className="absolute h-screen overflow-hidden flex flex-col bg-opacity-75 z-50 pointer-events-none"
                    style={{ width: debouncedWidth }}
                >
                    <div className="h-full overflow-hidden relative pointer-events-none">
                        {/* Insights and map controls */}
                        <DashboardControls />
                        {filterState.analytics.emissionHeatmaps.enabled &&
                            Object.values(selectedContext).some(
                                (v) => v !== undefined,
                            ) && (
                                <ControlContainer className="absolute top-2 right-2 z-50 gap-2 p-1 items-end pointer-events-auto">
                                    <div className="flex items-center pointer-events-auto bg-white/95 border border-neutral-200 shadow-sm rounded px-3 py-2 text-xs text-gray-700 max-w-xs gap-2">
                                        <FontAwesomeIcon
                                            icon={faTriangleExclamation}
                                            className="text-amber-500 mt-0.5"
                                        />
                                        <span>
                                            The heatmap is hidden while a
                                            selection is active. Clear the
                                            selection to view it.
                                        </span>
                                    </div>
                                </ControlContainer>
                            )}
                        {/* Bottom left controls */}
                        {/* TODO: Change all control paddings to 0.5 */}
                        <div className="absolute bottom-2 right-2 flex gap-2 items-end pointer-events-none">
                            {loading && <LoadingIndicator />}
                            <MapScale
                                latitude={_viewState.latitude}
                                zoom={_viewState.zoom}
                            />
                            <ZoomAndLegend
                                zoomIn={zoomIn}
                                zoomOut={zoomOut}
                                isMeasuring={isMeasuring}
                                toggleMeasuring={toggleMeasuring}
                            />
                        </div>
                    </div>
                    <EmissionsBrushChart />
                </div>
            </MapDataContext.Provider>
            {/* Map */}
            <div className="flex flex-col h-screen">
                <div className="relative h-full w-full overflow-hidden">
                    <MapBase
                        mapId="mainMap"
                        layers={[...layers, drawingLayer, measureLayers]}
                        cursor={isDrawing || isMeasuring ? "crosshair" : "grab"}
                        onRightClick={(info) => {
                            setSelectedContext({
                                ...selectedContext,
                                selectedLocation: {
                                    type: "Point",
                                    coordinates: info.coordinate,
                                },
                            });
                            copyToClipboard(
                                info.coordinate.toReversed().join(","),
                            );
                        }}
                        onLeftClick={({ info, event }) =>
                            onClickMap(info, event)
                        }
                    />
                </div>
                {pickedItems && (
                    <MapPopup
                        picked={pickedItems}
                        onClickItem={(item) => {
                            setPickedItems(undefined);
                            pickItem(item);
                        }}
                    />
                )}
            </div>
        </div>
    );
};
